/**
 * Static representative coordinates for each known connector.
 *
 * Use case: Delta map base layer. When `bus/events` returns no live points
 * (cold preview, no recent activity), we still want operators to *see* every
 * sensor on the world map. We anchor each connector to its operating HQ /
 * primary observation location / regional ops center.
 *
 * These are *symbolic* anchors, not the precise sensor footprint. Live event
 * points (with real lat/lon) take precedence whenever they exist.
 */

const CONNECTOR_LOCATIONS = {
  // ── Atlas (markets, geopolitics) ────────────────────────────────────────
  'atlas:acled':                { lat: 43.073, lon: -89.401, place: 'Madison, WI · ACLED HQ' },
  'atlas:baltic_dry_index':     { lat: 51.508, lon: -0.128,  place: 'London · Baltic Exchange' },
  'atlas:crypto_markets':       { lat: 37.775, lon: -122.418,place: 'San Francisco · Kraken' },
  'atlas:fred_economic_data':   { lat: 38.629, lon: -90.198, place: 'St. Louis Fed' },
  'atlas:fx_rates':             { lat: 51.508, lon: -0.128,  place: 'London · FX Desk' },
  'atlas:gdelt_global_events':  { lat: 38.901, lon: -77.034, place: 'Washington DC · GDELT' },
  'atlas:un_security_council':  { lat: 40.749, lon: -73.969, place: 'New York · UN HQ' },
  'atlas:world_bank_indicators':{ lat: 38.898, lon: -77.045, place: 'Washington DC · World Bank' },

  // ── Karbon ─────────────────────────────────────────────────────────────
  'karbon:mangrove_watch':      { lat: 24.700, lon: -78.000, place: 'Bahamas · Karbon AOI cluster' },

  // ── Nova (atmospheric, marine, environmental) ───────────────────────────
  'nova:allen_coral_atlas':     { lat: -27.471, lon: 153.027,place: 'Brisbane · Allen Coral Atlas' },
  'nova:chlorophyll_a':         { lat: -10.0,   lon: -50.0,  place: 'Atlantic / South Equatorial' },
  'nova:copernicus_cmems':      { lat: 43.604, lon: 1.444,   place: 'Toulouse · CMEMS' },
  'nova:gpm_imerg':             { lat: 38.995, lon: -76.853, place: 'Goddard · GPM IMERG' },
  'nova:nasa_firms':            { lat: 38.995, lon: -76.853, place: 'Goddard · NASA FIRMS' },
  'nova:noaa_tide_gauges':      { lat: 38.991, lon: -77.013, place: 'Silver Spring · NOAA CO-OPS' },
  'nova:vaac':                  { lat: 61.218, lon: -149.900,place: 'Anchorage VAAC' },
  'nova:cimh':                  { lat: 13.100, lon: -59.620, place: 'Barbados · CIMH' },
  'nova:coops':                 { lat: 38.991, lon: -77.013, place: 'NOAA CO-OPS' },
  'nova:coral_reef_watch':      { lat: 27.770, lon: -82.640, place: 'St. Petersburg · CRW' },
  'nova:eonet':                 { lat: 38.995, lon: -76.853, place: 'Goddard · EONET' },
  'nova:goes16':                { lat: 0.0,    lon: -75.200, place: 'GOES-16 · 75.2°W geostationary' },
  'nova:lightning':             { lat: -1.0,   lon: 30.0,    place: 'Africa Equatorial · lightning belt' },
  'nova:nasa_smap':             { lat: 34.200, lon: -118.170,place: 'JPL · SMAP' },
  'nova:ndbc':                  { lat: 30.367, lon: -89.617, place: 'Stennis · NDBC' },
  'nova:nhc':                   { lat: 25.792, lon: -80.200, place: 'Miami · National Hurricane Center' },
  'nova:noaa_ndbc':             { lat: 30.367, lon: -89.617, place: 'Stennis · NOAA NDBC' },
  'nova:nws':                   { lat: 38.985, lon: -76.939, place: 'College Park · NWS' },
  'nova:openaq':                { lat: 38.901, lon: -77.034, place: 'Washington DC · OpenAQ' },
  'nova:openmeteo':             { lat: 47.398, lon: 8.648,   place: 'Switzerland · Open-Meteo' },
  'nova:rainviewer':            { lat: 50.450, lon: 30.523,  place: 'Kyiv · RainViewer' },
  'nova:sentinel1_sar':         { lat: 41.829, lon: 12.671,  place: 'Frascati · ESA ESRIN' },
  'nova:usgs':                  { lat: 38.948, lon: -77.357, place: 'Reston · USGS' },
  'nova:usgs_water':            { lat: 38.948, lon: -77.357, place: 'Reston · USGS Water' },

  // ── Orion (space weather) ───────────────────────────────────────────────
  'orion:ace_solar_wind':       { lat: 38.995, lon: -76.853, place: 'Goddard · ACE Solar Wind' },
  'orion:dscovr':               { lat: 40.015, lon: -105.270,place: 'Boulder · NOAA SWPC / DSCOVR' },
  'orion:celestrak':            { lat: 38.834, lon: -104.821,place: 'Colorado Springs · CelesTrak' },
  'orion:iss_tracker':          { lat: 29.553, lon: -95.099, place: 'Houston · NASA JSC · ISS' },
  'orion:nasa_apod':            { lat: 38.995, lon: -76.853, place: 'Goddard · NASA APOD' },
  'orion:nasa_donki':           { lat: 38.995, lon: -76.853, place: 'Goddard · DONKI / CCMC' },
  'orion:noaa_swpc':            { lat: 40.015, lon: -105.270,place: 'Boulder · NOAA SWPC' },

  // ── Vega (infrastructure) ───────────────────────────────────────────────
  'vega:ais_ship_tracking':     { lat: 37.984, lon: 23.728,  place: 'Athens · MarineTraffic' },
  'vega:ais_vessels':           { lat: 37.984, lon: 23.728,  place: 'Athens · MarineTraffic' },
  'vega:cloudflare_radar':      { lat: 37.775, lon: -122.418,place: 'San Francisco · Cloudflare' },
  'vega:flightaware':           { lat: 29.760, lon: -95.366, place: 'Houston · FlightAware' },
  'vega:lightning':             { lat: -3.0,   lon: 25.0,    place: 'Congo Basin · global lightning' },
  'vega:metar_taf':             { lat: 39.099, lon: -94.578, place: 'Kansas City · NWS AWC' },
  'vega:traffic_incidents':     { lat: 51.508, lon: -0.128,  place: 'Global traffic feed · London' },
  'vega:usgs_hazards':          { lat: 38.948, lon: -77.357, place: 'Reston · USGS Hazards' },
};

const HEALTH_COLORS = {
  active:   '#00e5b0',
  ok:       '#00e5b0',
  degraded: '#f59e0b',
  offline:  '#fb7185',
  unknown:  '#94a3b8',
};

/** Map registry rows → static map points the Delta map can render as a base layer. */
export function buildRegistryMapPoints(registry) {
  if (!Array.isArray(registry)) return [];
  const out = [];
  for (const row of registry) {
    const anchor = CONNECTOR_LOCATIONS[row.sensor_id];
    if (!anchor) continue;
    const health = String(row.health || 'unknown').toLowerCase();
    const color = HEALTH_COLORS[health] || HEALTH_COLORS.unknown;
    out.push({
      sensor_id: row.sensor_id,
      lat: anchor.lat,
      lon: anchor.lon,
      place: anchor.place,
      name: row.name,
      agent: row.agent,
      domain: row.domain,
      health,
      color,
      kind: 'registry',
    });
  }
  return out;
}

export const CONNECTOR_HEALTH_COLORS = HEALTH_COLORS;
